import { IPCEventType } from '@electron/shared/ipc.types';
import {
  type MeetingTranscriptionInputSelection,
  type MeetingTranscriptionInputDevicesPayload,
  MeetingTranscriptionInputDevicesPayloadSchema,
} from '@electron/shared/meetings.types';

import { ElectronAPI } from '../base/ElectronAPI';

import type { ElectronUnsubscribe } from '../base/types';

export enum TranscriptionInputDeviceErrorCode {
  InvalidDevice = 'INVALID_TRANSCRIPTION_INPUT_DEVICE',
  SelectionUnavailable = 'AUDIO_INPUT_DEVICE_SELECTION_UNAVAILABLE',
  // Feedback: device selection should not require an active meeting
  NoMeetingInProgress = 'NO_MEETING_IN_PROGRESS',
  RequestFailed = 'REQUEST_FAILED',
}

const displayMessages: Record<TranscriptionInputDeviceErrorCode, string> = {
  [TranscriptionInputDeviceErrorCode.InvalidDevice]:
    'The selected device is no longer available',
  [TranscriptionInputDeviceErrorCode.SelectionUnavailable]:
    'Input device selection is not available',
  [TranscriptionInputDeviceErrorCode.NoMeetingInProgress]:
    'No meeting in progress',
  [TranscriptionInputDeviceErrorCode.RequestFailed]:
    'Failed to update input device',
};

const errorCodeValues = new Set<string>(
  Object.values(TranscriptionInputDeviceErrorCode)
);

function parseErrorCode(info: string | null | undefined): {
  code: TranscriptionInputDeviceErrorCode;
  reason: string | undefined;
} {
  if (info && errorCodeValues.has(info)) {
    return {
      code: info as TranscriptionInputDeviceErrorCode,
      reason: undefined,
    };
  }
  return {
    code: TranscriptionInputDeviceErrorCode.RequestFailed,
    reason: info ?? undefined,
  };
}

export class TranscriptionInputDeviceError extends Error {
  constructor(
    public readonly code: TranscriptionInputDeviceErrorCode,
    reason?: string
  ) {
    super(reason ?? code);
    this.name = 'TranscriptionInputDeviceError';
    Object.setPrototypeOf(this, new.target.prototype);
  }

  get displayMessage(): string {
    return displayMessages[this.code];
  }
}

/**
 * Base `ElectronAPI.addListener` is one-listener-per-eventType, so this class
 * fans a single underlying device-change subscription out to multiple consumers.
 */
export class TranscriptionInputDeviceAPI extends ElectronAPI {
  private subscribers = new Map<
    number,
    (payload: MeetingTranscriptionInputDevicesPayload) => void
  >();
  private nextSubscriberId = 0;
  private listenerUnsubscribe?: ElectronUnsubscribe;

  async getDevices() {
    const response = await this.requestContextKit(
      IPCEventType.meetingGetTranscriptionInputDevices
    );

    if (response?.type === 'error') {
      const { code, reason } = parseErrorCode(response.info);
      throw new TranscriptionInputDeviceError(code, reason);
    }

    if (!response?.info) {
      throw new TranscriptionInputDeviceError(
        TranscriptionInputDeviceErrorCode.RequestFailed,
        'Empty response from getTranscriptionInputDevices'
      );
    }

    try {
      return MeetingTranscriptionInputDevicesPayloadSchema.parse(
        JSON.parse(response.info)
      );
    } catch {
      throw new TranscriptionInputDeviceError(
        TranscriptionInputDeviceErrorCode.RequestFailed,
        'Invalid response from getTranscriptionInputDevices'
      );
    }
  }

  async setDevice(selection: MeetingTranscriptionInputSelection) {
    const response = await this.requestContextKit(
      IPCEventType.meetingSetTranscriptionInputDevice,
      { selection }
    );

    if (response?.type === 'error') {
      const { code, reason } = parseErrorCode(response.info);
      throw new TranscriptionInputDeviceError(code, reason);
    }
  }

  private setupListener() {
    if (this.listenerUnsubscribe) return;

    this.listenerUnsubscribe = this.addListener({
      eventType: IPCEventType.meetingTranscriptionInputDeviceChanged,
      payloadValidator: MeetingTranscriptionInputDevicesPayloadSchema,
      callback: (payload) => {
        for (const subscriber of Array.from(this.subscribers.values())) {
          try {
            subscriber(payload);
          } catch (error) {
            console.error(
              'TranscriptionInputDeviceAPI subscriber failed to handle device change',
              error
            );
          }
        }
      },
    });
  }

  onDeviceChanged(
    callback: (payload: MeetingTranscriptionInputDevicesPayload) => void
  ): ElectronUnsubscribe {
    const subscriberId = this.nextSubscriberId++;

    this.subscribers.set(subscriberId, callback);
    this.setupListener();

    return () => {
      this.subscribers.delete(subscriberId);

      if (this.subscribers.size === 0) {
        this.listenerUnsubscribe?.();
        this.listenerUnsubscribe = undefined;
      }
    };
  }

  override clearAllListeners(): void {
    this.subscribers.clear();
    this.nextSubscriberId = 0;
    this.listenerUnsubscribe?.();
    this.listenerUnsubscribe = undefined;
    super.clearAllListeners();
  }
}
